import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { apiConfig } from './api.config'
import { RuntimeConfig } from '../../config/runtime.config'
import { PaymentListResponse } from '../../../features/payment/types/payment.types'

// Custom request config type
interface CustomRequestConfig
  extends Omit<AxiosRequestConfig, 'url' | 'method'> {
  useAuthToken?: boolean
}

// Auth response type
export interface AuthResponse {
  success: boolean
  token: string
  expiresIn: number
  user: {
    id: number
    username: string
  }
}

// Payment request type
export interface PaymentRequest {
  transaction_id: string
  account: string
  provider: string
  amount?: number
}

// Payment response type
export interface PaymentResponse {
  transaction_id: string
  message?: string[]
  message_code: string[]
  data: {
    amount: number
    phone: string
    account: string
    provider: string
  }
  promo?: string
  code: number
}

export class ApiService {
  private token: string | null = null

  constructor(private instance: AxiosInstance) {}

  // Auth token management
  public setAuthToken(token: string | null): void {
    this.token = token
  }

  /**
   * Generic request method that handles authentication
   */
  private async request<T = any>(
    config: AxiosRequestConfig,
    options: CustomRequestConfig = {},
  ): Promise<T> {
    const requestConfig: AxiosRequestConfig = { ...config }

    // Add auth header if needed
    if (options.useAuthToken !== false && this.token) {
      requestConfig.headers = {
        ...requestConfig.headers,
        Authorization: `Bearer ${this.token}`,
      }
    }

    try {
      const response = await this.instance.request<T>(requestConfig)
      return response.data
    } catch (error) {
      throw error
    }
  }

  // Auth endpoint - login only
  public login(data: any): Promise<AuthResponse> {
    return this.request<AuthResponse>(
      {
        method: apiConfig.endpoints.auth.login.method,
        url: apiConfig.endpoints.auth.login.url,
        data,
      },
      {
        useAuthToken: apiConfig.endpoints.auth.login.auth,
      },
    )
  }

  // Payment endpoints
  public createPayment(data: PaymentRequest): Promise<PaymentResponse> {
    return this.request<PaymentResponse>(
      {
        method: apiConfig.endpoints.payments.create.method,
        url: apiConfig.endpoints.payments.create.url,
        data,
      },
      {
        useAuthToken: apiConfig.endpoints.payments.create.auth,
      },
    )
  }

  public getPayments(): Promise<PaymentListResponse> {
    return this.request<PaymentListResponse>(
      {
        method: apiConfig.endpoints.payments.list.method,
        url: apiConfig.endpoints.payments.list.url,
      },
      {
        useAuthToken: apiConfig.endpoints.payments.list.auth,
      },
    )
  }
}

let apiService: ApiService
let axiosInstance: AxiosInstance

export function initApiService(runtimeConfig: RuntimeConfig) {
  axiosInstance = axios.create({
    baseURL: runtimeConfig.react_app_api_url,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  apiService = new ApiService(axiosInstance)
}

export function getApiService() {
  return apiService
}

export function getAxiosInstance() {
  return axiosInstance
}
